import ErrorMessage from "components/common/ErrorMessage";
import LiveClock from "components/common/LiveClock";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { fetchScheduleSummaryList } from "slices/scheduleSlice";
import { Select } from "components/common/Select";
import ListTable from "components/dashboard/ListTable";
import { fetchDashboardAnalytics } from "slices/dashboardSlice";
import { fetchDashboardAppointments } from "slices/dashboardSlice";
import { DashboardSelector } from "slices/dashboardSlice";
import { connectQuickBooks } from "slices/dashboardSlice";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { fetchDepartments } from "slices/departmentsSlice";
import { departmentsSelector } from "slices/departmentsSlice";
import { fetchFacilities } from "slices/facilitiesSlice";
import { facilitiesSelector } from "slices/facilitiesSlice";
import AddAppointment from "components/appointment/AddAppointment";
import { fetchAppointments } from "slices/appointmentSlice";
import { setError } from "slices/dashboardSlice";
import { fetchInterpreters } from "slices/interpreterSlice";
import { AsyncPaginate } from "react-select-async-paginate";
import { selectCustomStyles } from "components/common/Select";
import FullPageLoader from "components/common/FullPageLoader";
import { usePermissions } from "layouts/Admin";
import { LegendButton, getLegends } from "components/common/TableColor";
import { userSelector } from "slices/userSlice";
import { Link } from "react-router-dom";

const DatesInputs = ({ control, errors }) => {
  const startDateRef = React.useRef(null);
  const endDateRef = React.useRef(null);
  const { t } = useTranslation();

  return (
    <>
      <div className="row-primary">
        <h2>{t("dashboard.startDate")}</h2>
        <Controller
          name="start_date"
          control={control}
          render={({ field }) => (
            <div className="input-image basic_bases">
              <input
                ref={startDateRef}
                id="start_date"
                type="date"
                name="start_date"
                placeholder="Start Date"
                {...field}
                value={
                  field.value ? moment(field.value).format("YYYY-MM-DD") : ""
                }
                onChange={(e) => {
                  const dateString = moment(new Date(e.target.value)).format(
                    "YYYY/MM/DD"
                  );
                  field.onChange(dateString);
                }}
              />
              <img
                src="/images/celender.png"
                alt="celender Icon"
                className="custom-icon"
              />
            </div>
          )}
        />
      </div>
      <div className="row-primary">
        <h2>{t("dashboard.endDate")}</h2>
        <Controller
          name="end_date"
          control={control}
          render={({ field }) => (
            <div className="input-image basic_bases">
              <input
                ref={endDateRef}
                type="date"
                name="end_date"
                placeholder="End Date"
                {...field}
                value={
                  field.value ? moment(field.value).format("YYYY-MM-DD") : ""
                }
                onChange={(e) => {
                  const dateString = moment(new Date(e.target.value)).format(
                    "YYYY/MM/DD"
                  );
                  field.onChange(dateString);
                }}
              />
              <img
                src="/images/celender.png"
                alt="celender Icon"
                className="custom-icon"
              />
            </div>
          )}
        />
      </div>
    </>
  );
};

function Dashboard() {
  const { checkPermission } = usePermissions();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    dashboard,
    last_page: totalPages,
    loader,
    total,
  } = useSelector(DashboardSelector);
  const { language: languages } = useSelector(languageSelector);
  const { departments } = useSelector(departmentsSelector);
  const { facilities } = useSelector(facilitiesSelector);
  const [pageLength, setPageLength] = useState(10);
  const {
    clearErrors,
    handleSubmit,
    watch,
    formState: { errors },
    control,
    setValue,
    setError: setErrors,
    reset,
  } = useForm({
    defaultValues: {
      start_date: moment().startOf("month").format("YYYY-MM-DD"),
      end_date: moment().endOf("month").format("YYYY-MM-DD"),
    },
  });

  const [statusValue, setStatusValue] = React.useState("all");
  const [filters, setFilters] = useState({
    status: statusValue,
    start_date: moment().startOf("month").format("YYYY-MM-DD"),
    end_date: moment().endOf("month").format("YYYY-MM-DD"),
    service: "",
    language_id: "",
  });

  const [interpreterState, setInterpreterState] = useState([]);

  const [activeLegend, setActiveLegend] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(fetchDashboardAnalytics());
    dispatch(
      fetchDashboardAppointments(1, {
        pageLength: 10,
        start_date: moment().startOf("month").format("YYYY-MM-DD"),
        end_date: moment().endOf("month").format("YYYY-MM-DD"),
        sortBy: "id",
        sortDirection: "desc",
      })
    );
  }, []);

  // #region Language dropdown
  const filteredLanguages = React.useMemo(
    () =>
      languages?.map((language) => {
        const object = {
          value: language?.id,
          label: language?.name,
        };
        return object;
      }),
    [languages]
  );

  const filteredLanguagesLoadOptions = async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchLanguages(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion

  //#region Department dropdown
  const filtereddepartment = React.useMemo(
    () =>
      departments?.map((department) => {
        const object = {
          value: department?.id,
          label: department?.name,
        };
        return object;
      }),
    [departments]
  );
  const filtereddepartmentLoadOptions = async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchDepartments(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion

  //#region Facility dropdown
  const filteredFacilities = React.useMemo(
    () =>
      facilities?.map((facility) => {
        const object = {
          value: facility?.id,
          label: facility?.abbreviation,
        };
        return object;
      }),
    [facilities]
  );

  const filteredFacilitiesLoadOptions = async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchFacilities(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.abbreviation,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion

  //#region Interpreter dropdown
  const filteredInterpreterLoadOptions = async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchInterpreters(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );

    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.user.name,
      })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };
  //#endregion

  // #region Legends
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...getLegends(t)];
    temp_array.unshift({
      value: "all",
      name: t("dashboard.legend.all"),
      id: 0,
      color: "#3176A0",
    });
    return temp_array;
  }, [t]);

  const hanldeActiveLegend = (id, value) => {
    setActiveLegend(id);
    setStatusValue(value);
    setPageLength(10);
    setSearchTerm("");

    const newFilters = {
      ...filters,
      pageLength: 10,
      status: value,
    };
    setFilters(newFilters);

    dispatch(fetchDashboardAppointments(1, { ...newFilters, sortBy: 'id', sortDirection: 'desc' }));
  };

  //#endregion

  const onSubmit = (data) => {
    setSearchTerm("");
    const newFilters = {
      ...filters,
      ...data,
      pageLength: pageLength,
    };
    setFilters(newFilters);
    dispatch(fetchDashboardAppointments(1, { ...newFilters }));
  };

  const handleResetFields = () => {
    const defaultStartDate = moment().startOf("month").format("YYYY-MM-DD");
    const defaultEndDate = moment().endOf("month").format("YYYY-MM-DD");
    // setValue("start_date", null);
    // setValue("end_date", null);
    setValue("start_date", moment().startOf("month").format("YYYY-MM-DD"));
    setValue("end_date", moment().endOf("month").format("YYYY-MM-DD"));
    setValue("language_id", "");
    setValue("interpreter", "");
    setInterpreterState([]);
    setValue("department_id", "");
    setValue("facility_id", "");
    setValue("service", "");

    const resetFilters = {
      status: "all",
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      service: "",
      language_id: "",
      interpreter: "",
      department_id: "",
      facility_id: "",
      pageLength: 10,
    };
    setFilters(resetFilters);
    setActiveLegend(0);
    setStatusValue("all");

    dispatch(
      fetchDashboardAppointments(1, { sortBy: "id", sortDirection: "desc" })
    );
    clearErrors();
  };

  const serviceOption = [
    { value: "In Person", label: t("dashboard.service.inPerson") },
    { value: "Telephonic", label: t("dashboard.service.telephonic") },
    {
      value: "Video Remote Interpretation",
      label: t("dashboard.service.videoRemote"),
    },
    { value: "OPI On Demand", label: t("dashboard.service.opiDemand") },
  ];
  const [showWebPhone, setShowWebPhone] = useState(false);

  const openWebPhone = () => {
    setShowWebPhone(!showWebPhone);
    // window?.WebPhoneSDK && new window.WebPhoneSDK({
    //   parentElement: '#webphone',
    //   just_url: "telephony.algovisolutions.com",
    //   wss_url: 'wss://telephony.algovisolutions.com:8089/asterisk/ws',
    //   extension: '2000',
    //   extension_password: '2IpN1MFZgeWOaQzYU1At',
    //   from_cid: '9218764328',
    //   mediaElement: 'media_player',
    //   callCardElement: 'callCards',
    //   attempt_auto_register: true
    // })
  };

  const [isFourteenDays, setIsFourteenDays] = useState(false);
  const tableRef = useRef(null);

  const handleFilter = (item, isFourteen) => {
    const today = moment().format("YYYY-MM-DD");
    const nextFourteenDays = moment().add(14, "days").format("YYYY-MM-DD");

    const startDate = today;
    const endDate = isFourteen ? nextFourteenDays : today;

    setValue("start_date", startDate);
    setValue("end_date", endDate);
    setValue("service", item?.type || "");

    setActiveLegend(0);
    setStatusValue("available,assigned");

    const newFilters = {
      ...filters,
      start_date: today,
      end_date: endDate,
      service: item?.type || "",
      pageLength: pageLength,
      status: "available,assigned",
    };

    setFilters(newFilters);
    dispatch(fetchDashboardAppointments(1, { ...newFilters }));
    tableRef.current.scrollIntoView({ behavior: "smooth" });
    dispatch(setError({}));
    clearErrors();
  };

  const { user } = useSelector(userSelector);
  const checkinStatus = Number(user?.checked_in ?? user?.user?.checked_in) === 1 ? 1 : 0;
  const userRole = user?.role ?? user?.user?.role;

  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">{t("dashboard.title")}</h2>
          <div>
            {checkPermission("Webphone Access") && checkinStatus === 1 && (
              <button
                className="Cancle_button me-2"
                type="button"
                data-bs-toggle="offcanvas"
                data-bs-target="#offcanvasExample"
                aria-controls="offcanvasExample"
                onClick={openWebPhone}
              >
                {t("dashboard.webPhone")}
              </button>
            )}
            {userRole === "admin" && (
              <Link to={`${process.env.REACT_APP_BACKEND_URL_LOCAL}/api/quickbooks/connect`} target="_blank" className="main_button  green me-2" type="button">{t("dashboard.quickBooks")}</Link>
            )}
          </div>
        </div>

        <div className="row_dash_1">
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>{t("dashboard.today")}</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">{t("dashboard.today")}</th>
                    <th className="col-3 text-center">{t("dashboard.registered")}</th>
                    <th className="col-3 text-center">{t("dashboard.toCover")}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr
                      className="cursorPointer"
                      key={index}
                      onClick={() => {
                        setIsFourteenDays(false);
                        handleFilter(service, false);
                      }}
                    >
                      <td>{service?.type}</td>
                      <td>{service?.registered}</td>
                      <td>{service?.cover}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card_table">
            <div className="interpreter p-4">
              <h2>{t("dashboard.next14Days")}</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">{t("dashboard.today")}</th>
                    <th className="col-3 text-center">{t("dashboard.registered")}</th>
                    <th className="col-3 text-center">{t("dashboard.toCover")}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map(
                    (service, index) => (
                      <tr
                        className="cursorPointer"
                        key={index}
                        onClick={() => {
                          setIsFourteenDays(true);
                          handleFilter(service, true);
                        }}
                      >
                        <td>{service?.type}</td>
                        <td>{service?.registered}</td>
                        <td>{service?.cover}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* <ScheduleModal /> */}

        <div className="card_table">
          <div className="button-sm forLegends p-3">
            <LegendButton
              activeId={activeLegend}
              options={filteredStatuses}
              handleClick={hanldeActiveLegend}
            />
          </div>
          <form className="search_form_ad">
            <div className="row-primary dahboard_filter">
              <DatesInputs control={control} errors={errors} />
              <div className="row-primary">
                <h2>{t("dashboard.vendor")}</h2>
                <Controller
                  name="interpreter"
                  control={control}
                  render={({ field }) => (
                    <div className="basic_bases">
                      <AsyncPaginate
                        menuPlacement="auto"
                        isMulti
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={interpreterState || []}
                        loadOptions={filteredInterpreterLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          setInterpreterState(obj);
                          field.onChange(obj.map((opt) => opt.value));
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                      {errors?.interpreter?.message && (
                        <div style={{ marginBottom: 10 }}>
                          <ErrorMessage text={errors?.interpreter?.message} />
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
              {/* <div className="row-primary">
                <h2>Facility</h2>
                <Controller
                  name="facility_id"
                  control={control}
                  render={({ field }) => (
                    <div className="basic_bases">
                      <AsyncPaginate
                        menuPlacement="auto"
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={
                          field.value
                            ? filteredFacilities?.find(
                                (opt) => field.value == opt.value
                              )
                            : ""
                        }
                        loadOptions={filteredFacilitiesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  )}
                />
              </div> */}
              <div className="row-primary">
                <h2>{t("dashboard.language")}</h2>
                <Controller
                  name="language_id"
                  control={control}
                  render={({ field }) => (
                    <div className="basic_bases">
                      <AsyncPaginate
                        menuPlacement="auto"
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={
                          field.value
                            ? filteredLanguages?.find(
                              (opt) => field.value == opt.value
                            )
                            : ""
                        }
                        loadOptions={filteredLanguagesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  )}
                />
              </div>
              <div className="row-primary">
                <h2>{t("dashboard.services")}</h2>
                <Controller
                  name="service"
                  control={control}
                  render={({ field }) => (
                    <div className="basic_bases">
                      <Select
                        name="service"
                        options={serviceOption || []}
                        {...field}
                        value={
                          field.value
                            ? serviceOption?.find(
                              (opt) => field.value == opt.value
                            )
                            : ""
                        }
                      />
                    </div>
                  )}
                />
              </div>
              {/* <div className="row-primary">
                <h2>Department</h2>
                <Controller
                  name="department_id"
                  control={control}
                  render={({ field }) => (
                    <div className="basic_bases">
                      <AsyncPaginate
                        menuPlacement="auto"
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={
                          field.value
                            ? filtereddepartment?.find(
                                (opt) => field.value == opt.value
                              )
                            : ""
                        }
                        loadOptions={filtereddepartmentLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  )}
                />
              </div> */}
            </div>

            <div className="row-primary btns_area row_10">
              <SubmitButton
                handleSubmit={handleSubmit(onSubmit)}
                buttonType="button"
                btnText={t("dashboard.search")}
                className={"green"}
              />
              <SubmitButton
                buttonType="button"
                className={"lightBlue"}
                btnText={t("dashboard.reset")}
                handleSubmit={handleResetFields}
              />
            </div>

            <div ref={tableRef}>
              <ListTable
                data={dashboard?.appointments}
                filters={filters}
                setFilters={setFilters}
                setPageLength={setPageLength}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
          </form>
        </div>
      </div>

      {loader && <FullPageLoader />}
    </>
  );
}

export default Dashboard;
